import { Tabs, Tab, Typography } from '@mui/material';
import React, { useState } from 'react';
import { TabItem, TabsComponentProps, TabVariant } from '@models/tabs.model.ts';
import { useTranslation } from 'react-i18next';
import {
  CloseIcon,
  EditBlackIcon
} from '@shared/components/icons/svg-icon.tsx';
import {
  segmentedCompactTabSx,
  segmentedSingleTabSx,
  segmentedSingleTabsSx,
  segmentedTabItemSx,
  segmentedTabsSx
} from '@styles/components/mui-tabs-styles.ts';

const TabsComponent: React.FC<TabsComponentProps> = ({
  value,
  onChange,
  tabs,
  variant = TabVariant.Default,
  segmentedCompact = false,
  disabled = {},
  onDelete,
  onEdit
}) => {
  const { t } = useTranslation();
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const isSegmented = variant === TabVariant.Segmented;
  const segmentedSingleTab = isSegmented && tabs.length === 1;

  const handleDeleteClick = (e: React.MouseEvent, tab: TabItem) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(tab);
    }
  };

  const handleEditClick = (e: React.MouseEvent, tab: TabItem) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(tab);
    }
  };

  const renderDefaultLabel = (tab: TabItem) => (
    <div className="flex items-center gap-2">
      <Typography variant="inherit" component="span">
        {t(tab.label)}
      </Typography>
      <div className="flex items-center gap-1">
        {tab.showEditIcon && (
          <div className="flex items-center justify-center w-5 h-5">
            {onEdit && hoveredTab === tab.value && (
              <div
                onClick={(e) => handleEditClick(e, tab)}
                className="cursor-pointer p-1 rounded-full hover:bg-(--gray-10) transition-colors flex items-center justify-center w-5 h-5"
              >
                <EditBlackIcon style={{ width: '13px', height: '13px' }} />
              </div>
            )}
          </div>
        )}
        {tab.showDeleteIcon && (
          <div className="flex items-center justify-center w-5 h-5">
            {onDelete && hoveredTab === tab.value && (
              <div
                onClick={(e) => handleDeleteClick(e, tab)}
                className="cursor-pointer p-1 rounded-full hover:bg-(--gray-10) transition-colors flex items-center justify-center w-5 h-5"
              >
                <CloseIcon style={{ width: '10px', height: '10px' }} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const getTabIcon = (
    tab: TabItem
  ): string | React.ReactElement | undefined => {
    if (isSegmented) {
      if (!React.isValidElement(tab.icon)) return undefined;
      if (typeof tab.icon.type === 'string') return tab.icon;
      return React.cloneElement(
        tab.icon as React.ReactElement<{ stroke?: string }>,
        {
          stroke: tab.value === value ? 'var(--primary)' : 'var(--gray-2)'
        }
      );
    }
    return tab?.flag ? (
      <img
        className="max-h-5 h-5 w-5 mr-2 rounded-4xl object-cover"
        src={tab.flag}
        alt={t('tabs_flag_alt')}
      />
    ) : (
      ''
    );
  };

  return (
    <div
      className={
        isSegmented
          ? `inline-flex items-center bg-(--gray-11) border border-(--gray-9) rounded-[10px] p-0.75 ${
              segmentedSingleTab ? 'w-fit max-w-full self-start' : 'w-full'
            }`
          : 'inline-flex relative'
      }
    >
      <Tabs
        value={value}
        onChange={onChange}
        aria-label={t('tabs_navigation_aria_label')}
        sx={
          isSegmented
            ? {
                ...segmentedTabsSx,
                ...(segmentedSingleTab ? segmentedSingleTabsSx : {})
              }
            : undefined
        }
      >
        {tabs.map((tab) => (
          <Tab
            disableRipple
            disabled={disabled[tab.value] || false}
            value={tab.value}
            data-tour={tab.dataTour}
            iconPosition={isSegmented ? 'start' : 'top'}
            onMouseEnter={
              !isSegmented ? () => setHoveredTab(tab.value) : undefined
            }
            onMouseLeave={!isSegmented ? () => setHoveredTab(null) : undefined}
            icon={getTabIcon(tab)}
            label={isSegmented ? t(tab.label) : renderDefaultLabel(tab)}
            key={tab.value}
            sx={
              isSegmented
                ? {
                    ...segmentedTabItemSx,
                    ...(segmentedSingleTab ? segmentedSingleTabSx : {}),
                    ...(segmentedCompact ? segmentedCompactTabSx : {})
                  }
                : { textTransform: 'none' }
            }
          />
        ))}
      </Tabs>
      {!isSegmented && (
        <div
          className={`absolute bottom-0.375 max-lg-xl:bottom-px z-1 h-px w-full ${
            disabled ? 'bg-(--gray-8)' : 'bg-(--blue-12)'
          }`}
        />
      )}
    </div>
  );
};

export default TabsComponent;
